import React from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Checkbox,
  CloseButton,
  Group,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconRefresh, IconTrash } from '@tabler/icons-react';
import type { CalendarWeekRecord } from '../../../api/schedulingApis';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';

interface WeekDraft {
  dayOfWeek: number;
  shiftNumber: number;
  isWorking: boolean;
  dayStartTime: string;
  dayEndTime: string;
  workingMinutes: number;
}

interface CalendarWeekRulesTabProps {
  calendarSelected: boolean;
  tableViewportHeight: string;
  dayOptions: Array<{ value: string; label: string }>;
  weekSearch: string;
  setWeekSearch: React.Dispatch<React.SetStateAction<string>>;
  filteredWeeks: CalendarWeekRecord[];
  weekDraft: WeekDraft;
  setWeekDraft: React.Dispatch<React.SetStateAction<WeekDraft>>;
  onAddRule: () => Promise<void>;
  onSyncRule: (rule: CalendarWeekRecord) => Promise<void>;
  onDeleteRule: (ruleId: string) => Promise<void>;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export default function CalendarWeekRulesTab(props: CalendarWeekRulesTabProps) {
  const {
    calendarSelected,
    tableViewportHeight,
    dayOptions,
    weekSearch,
    setWeekSearch,
    filteredWeeks,
    weekDraft,
    setWeekDraft,
    onAddRule,
    onSyncRule,
    onDeleteRule,
    canCreate = true,
    canEdit = true,
    canDelete = true,
  } = props;

  return (
    <Card withBorder radius="md" p="md" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack style={{ flex: 1, minHeight: 0 }}>
        <Group align="stretch" wrap="nowrap" gap="md">
          <Box style={{ flex: '1 1 64%', minWidth: 0 }}>
            <Group justify="space-between" align="center" h="100%" wrap="nowrap">
              <Title order={4}>Weekly rules</Title>
              <TextInput
                placeholder="Search by day, time, minutes..."
                value={weekSearch}
                onChange={(e) => setWeekSearch(e.currentTarget.value)}
                size="xs"
                rightSection={
                  weekSearch ? (
                    <CloseButton aria-label="Clear search" onClick={() => setWeekSearch('')} />
                  ) : null
                }
                w={{ base: '100%', sm: 260 }}
              />
            </Group>
          </Box>
          <Box style={{ flex: '0 1 32%', minWidth: 280 }}>
            <Group justify="flex-start" align="center" h="100%">
              <Text size="sm" fw={600}>Add new rule</Text>
            </Group>
          </Box>
        </Group>
        <Group align="stretch" wrap="nowrap" gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Box style={{ flex: '1 1 64%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
              <ScrollArea h={tableViewportHeight} type="auto" offsetScrollbars>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: 'var(--mantine-color-body)',
                    }}
                  >
                    <Table.Tr>
                      <Table.Th>Day</Table.Th><Table.Th>Shift</Table.Th><Table.Th>Working</Table.Th>
                      <Table.Th>Start</Table.Th><Table.Th>End</Table.Th><Table.Th>Minutes</Table.Th><Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredWeeks.length > 0 ? (
                      filteredWeeks.map((w) => (
                        <Table.Tr key={w.id}>
                          <Table.Td>{dayOptions.find((d) => Number(d.value) === w.dayOfWeek)?.label || w.dayOfWeek}</Table.Td>
                          <Table.Td>{w.shiftNumber ?? 1}</Table.Td>
                          <Table.Td>{w.isWorking ? 'Yes' : 'No'}</Table.Td>
                          <Table.Td>{w.dayStartTime}</Table.Td>
                          <Table.Td>{w.dayEndTime}</Table.Td>
                          <Table.Td>{w.workingMinutes}</Table.Td>
                          <Table.Td>
                            <Group gap={6} justify="flex-end">
                              <PermissionRestrictedTooltip restricted={!canEdit}>
                                <ActionIcon
                                  variant="light"
                                  disabled={!canEdit}
                                  onClick={() => { void onSyncRule(w); }}
                                >
                                  <IconRefresh size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                              <PermissionRestrictedTooltip restricted={!canDelete}>
                                <ActionIcon
                                  color="red"
                                  variant="light"
                                  disabled={!canDelete}
                                  onClick={() => { void onDeleteRule(w.id); }}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={7}>
                          <Text size="sm" c="dimmed" ta="center" py="lg" fw={600}>
                            {calendarSelected ? 'No rules to show' : 'Calendar not selected'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Box>
          <Card withBorder radius="md" p="sm" style={{ flex: '0 1 32%', minWidth: 280, height: '100%', minHeight: 0, overflow: 'auto' }}>
            <Stack>
              <Checkbox
                label="Working day"
                checked={weekDraft.isWorking}
                onChange={(e) => setWeekDraft((s) => ({ ...s, isWorking: e.currentTarget.checked }))}
              />
              <Select
                label="Day"
                data={dayOptions}
                value={String(weekDraft.dayOfWeek)}
                onChange={(v) => setWeekDraft((s) => ({ ...s, dayOfWeek: Number(v || 1) }))}
              />
              <NumberInput
                label="Shift #"
                value={weekDraft.shiftNumber}
                onChange={(v) =>
                  setWeekDraft((s) => ({
                    ...s,
                    shiftNumber: Math.min(4, Math.max(1, Number(v || 1))),
                  }))
                }
                min={1}
                max={4}
                clampBehavior="strict"
              />
              <TimeInput
                label="Start"
                value={weekDraft.dayStartTime}
                onChange={(e) => setWeekDraft((s) => ({ ...s, dayStartTime: e.currentTarget.value }))}
              />
              <TimeInput
                label="End"
                value={weekDraft.dayEndTime}
                onChange={(e) => setWeekDraft((s) => ({ ...s, dayEndTime: e.currentTarget.value }))}
              />
              <NumberInput
                label="Working minutes"
                value={weekDraft.workingMinutes}
                onChange={(v) => setWeekDraft((s) => ({ ...s, workingMinutes: Number(v || 0) }))}
                min={0}
              />
              <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
                <Button
                  disabled={!calendarSelected || !canCreate}
                  onClick={() => { void onAddRule(); }}
                  fullWidth
                >
                  Add rule
                </Button>
              </PermissionRestrictedTooltip>
            </Stack>
          </Card>
        </Group>
      </Stack>
    </Card>
  );
}
